import React, { useMemo } from 'react'
import {
    AppBar,
    Box,
    CircularProgress,
    Container,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    Toolbar,
    useScrollTrigger,
} from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Navbar from './Navbar'
import OrderFlowNavbar from './OrderFlowNavbar'
import {
    getCardType,
    getDeliveryGroupItems,
    getProductAltText,
    renderProductName,
    STEP_SMB_ORDER_FLOW_COBRAND_CARDS,
    STEP_SMB_ORDER_FLOW_LANDING,
} from '../../../../containers/SMBOrderFlow'
import isEqual from 'lodash/isEqual'
import CartPreviewDrawer from '../CartPreviewDrawer'
import Button from '@material-ui/core/Button'
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import { batch, useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import {
    markSmbCartItemForDeletion,
    setSmbCartFlyoutMode,
} from '../../../../actions/smbCart'

function ElevationScroll(props) {
    const { children } = props
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
    })

    return React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
    })
}

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 0,
        paddingRight: 0,
        [theme.breakpoints.up('lg')]: {
            padding: `0 ${theme.spacing(2)}px`,
        },
    },
    brandLogo: {
        [theme.breakpoints.up('sm')]: {
            width: 372,
        },
    },
    emptyCartContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
    },
    cartButton: {
        marginLeft: theme.spacing(3),
        fontSize: '1.2rem',
        [theme.breakpoints.up('md')]: {
            minWidth: 175,
        },
    },
    cartItemCount: {
        marginLeft: 5,
        fontSize: '12px',
        fontWeight: 'bold',
        border: '2px solid #FFFFFF',
        borderRadius: '50%',
        width: '22px',
        textAlign: 'center',
    },
    cartItemsList: {
        padding: `${theme.spacing(1)}px 0`,
        backgroundColor: '#FFFFFF',
    },
    cartItemContainer: {
        alignItems: 'flex-start',
        columnGap: '.8rem',
        borderBottom: `2px solid ${theme.palette.grey.main}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
        [theme.breakpoints.up('md')]: {
            padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
        },
    },
    cartItemImage: {
        width: '25%',
        marginTop: 5,
        borderRadius: '6px',
        flexGrow: 0,
    },
    cartItemDetails: {
        flexGrow: 1,
    },
    cartItemTotal: {
        flexGrow: 0,
    },
    cartItemDeleteButton: {
        color: theme.palette.grey[500],
    },
}))

const Header = (props) => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { baseUrl, logoImage, loading, step } = props
    const cartFlyoutMode = useSelector(({ smbCart }) => smbCart.cartFlyoutMode)
    const cartContents = useSelector(({ smbCart }) => smbCart.cartContents)
    let cartId, cartItems, deliveryGroups, orderTotal, totalShippingFees
    if (!isEmpty(cartContents)) {
        cartId = cartContents.cart_id
        cartItems = cartContents.cart_items
        deliveryGroups = cartContents.delivery_groups
        orderTotal = cartContents.order_total
        totalShippingFees = cartContents.total_shipping_fees
    }
    const cartItemCount = (deliveryGroups && deliveryGroups.length) || 0
    const cartSubtotal = (
        (cartId && orderTotal - totalShippingFees) ||
        0
    ).toFixed(2)
    const deliveryGroupCartItems = useMemo(
        () =>
            cartItems && deliveryGroups
                ? getDeliveryGroupItems(cartItems, deliveryGroups)
                : null,
        [cartItems, deliveryGroups]
    )

    const toggleCartPreview = () => (event) => {
        if (
            event.type === 'keydown' &&
            (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return
        }
        dispatch(setSmbCartFlyoutMode({ display: !cartFlyoutMode.display }))
    }

    const CartItems = () => (
        <div role="presentation">
            <List id="cart-items-list" className={classes.cartItemsList}>
                {cartFlyoutMode.addingToCart ? (
                    <ListItem className={`${classes.emptyCartContainer}`}>
                        <CircularProgress color="primary" />
                    </ListItem>
                ) : !isEmpty(deliveryGroupCartItems) ? (
                    deliveryGroupCartItems.map((deliveryGroupCartItem) => {
                        const {
                            deliveryGroupId,
                            product,
                            cards,
                            minGroupCardProcessingFee,
                            maxGroupCardProcessingFee,
                            priceWithProcessingFees,
                        } = deliveryGroupCartItem
                        return (
                            <ListItem
                                className={classes.cartItemContainer}
                                key={deliveryGroupId}
                            >
                                <img
                                    className={classes.cartItemImage}
                                    src={product.cardImageUrl}
                                    alt={getProductAltText(
                                        product.cardNetwork,
                                        product.isVirtual
                                    )}
                                    loading="lazy"
                                />
                                <Box className={classes.cartItemDetails}>
                                    <Typography>
                                        {renderProductName(
                                            product.cardNetwork,
                                            product.isVirtual
                                        )}
                                    </Typography>
                                    <Typography variant="body2">
                                        Type: {getCardType(product.cardType)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Card fee:{' '}
                                        {minGroupCardProcessingFee ===
                                        maxGroupCardProcessingFee
                                            ? `$${minGroupCardProcessingFee} each`
                                            : `$${minGroupCardProcessingFee} - $${maxGroupCardProcessingFee}`}
                                    </Typography>
                                    <Typography variant="body2">
                                        Quantity: {cards.length}
                                    </Typography>
                                </Box>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-end"
                                    className={classes.cartItemTotal}
                                >
                                    <Typography>
                                        ${priceWithProcessingFees}
                                    </Typography>
                                    <IconButton
                                        className={classes.cartItemDeleteButton}
                                        edge="end"
                                        aria-label="Delete cart item"
                                        onClick={() =>
                                            batch(() => {
                                                dispatch(
                                                    setSmbCartFlyoutMode({
                                                        display: false,
                                                    })
                                                )
                                                dispatch(
                                                    markSmbCartItemForDeletion({
                                                        openModal: true,
                                                        cartId,
                                                        item: deliveryGroupCartItem,
                                                    })
                                                )
                                            })
                                        }
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Box>
                            </ListItem>
                        )
                    })
                ) : (
                    <ListItem
                        className={`${classes.cartItemContainer} ${classes.emptyCartContainer}`}
                    >
                        <Typography variant="body1">
                            Your cart is empty.
                        </Typography>
                    </ListItem>
                )}
            </List>
        </div>
    )

    return (
        <>
            <ElevationScroll {...props}>
                <AppBar position="relative" style={{ background: '#FFF' }}>
                    <Toolbar>
                        <Container className={classes.wrapper}>
                            <a href={baseUrl} title="Back to homepage">
                                <img
                                    className={classes.brandLogo}
                                    src={logoImage}
                                    alt={'Brand Logo'}
                                    width={200}
                                    height={32}
                                />
                            </a>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <div style={{ color: 'black' }}>
                                    Available 24/7 at{' '}
                                    <span style={{ fontWeight: 'bold' }}>
                                        (855) 660-6847
                                    </span>
                                </div>
                                <Button
                                    className={classes.cartButton}
                                    onClick={toggleCartPreview()}
                                    component="label"
                                    aria-label="open cart preview drawer"
                                    variant={'contained'}
                                    color="primary"
                                >
                                    <ShoppingCartOutlinedIcon />
                                    <Hidden xsDown>
                                        <Typography
                                            style={{
                                                marginLeft: 5,
                                            }}
                                        >
                                            My cart
                                        </Typography>
                                        {cartItemCount > 0 && (
                                            <Typography
                                                className={
                                                    classes.cartItemCount
                                                }
                                            >
                                                {cartItemCount}
                                            </Typography>
                                        )}
                                    </Hidden>
                                </Button>
                            </div>
                            <CartPreviewDrawer
                                open={cartFlyoutMode.display}
                                addingToCart={cartFlyoutMode.addingToCart}
                                toggleCartPreview={toggleCartPreview}
                                cartItems={CartItems}
                                cartItemCount={cartItemCount}
                                cartSubtotal={cartSubtotal}
                            />
                        </Container>
                    </Toolbar>
                    {isEqual(step, STEP_SMB_ORDER_FLOW_LANDING) ||
                    isEqual(step, STEP_SMB_ORDER_FLOW_COBRAND_CARDS) ? (
                        <Navbar baseUrl={baseUrl} />
                    ) : (
                        <OrderFlowNavbar />
                    )}
                    {loading && <LinearProgress />}
                </AppBar>
            </ElevationScroll>
        </>
    )
}

export default Header
